import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider, App as AntApp, theme } from 'antd';
import trTR from 'antd/locale/tr_TR';
import enUS from 'antd/locale/en_US';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import 'dayjs/locale/en';
import { useTranslation } from 'react-i18next';
import '@/i18n';
import App from './App';
import './index.css';
import { useAuthStore } from '@/store/authStore';
import { useThemeStore } from '@/store/themeStore';

dayjs.locale(localStorage.getItem('mymaintenances-lang') || 'tr');

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 0,
    },
  },
});

function Root() {
  const lang = useAuthStore((s) => s.user?.language) || 'tr';
  const { i18n } = useTranslation();
  const currentLang = i18n.language || lang;
  const locale = currentLang === 'en' ? enUS : trTR;
  const palette = useThemeStore((s) => s.palette);
  const hydrate = useThemeStore((s) => s.hydrate);

  useEffect(() => { hydrate(); }, [hydrate]);
  useEffect(() => { dayjs.locale(currentLang === 'en' ? 'en' : 'tr'); }, [currentLang]);

  return (
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <ConfigProvider
          locale={locale}
          theme={{
            algorithm: palette.algorithm === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
            token: {
              colorPrimary: palette.colorPrimary,
              borderRadius: palette.borderRadius,
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            },
            components: {
              Layout: {
                siderBg: palette.siderBg,
                headerBg: palette.headerBg,
                bodyBg: palette.bodyBg,
              },
              Menu: {
                darkItemBg: palette.menuDarkItemBg,
                darkSubMenuItemBg: palette.menuDarkSubMenuItemBg,
              },
            },
          }}
        >
          <AntApp>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </AntApp>
        </ConfigProvider>
      </QueryClientProvider>
    </React.StrictMode>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(<Root />);
